import { throttle } from 'lodash';
import { MutableRefObject } from 'react';
import { TABLE_CELL_MIN_WIDTH } from '../../../../common/table/tableConstants';
import { stopDefaultAndPropagation } from '../../../utils/domUtil';
import { getResizeTargetColumns } from '../utils/selectionUtils/tableSelectionResizeColumnUtils';
import {
    applyManualColumnWidths,
    applyManualColumnWidthsWithHotHooks,
} from '../utils/hotInstanceUtils/columnSize/hotTableManualColumnResizeApplyUtils';
import {
    clearManualColumnLiveResizeSession,
    getManualColumnLiveResizeSession,
    initialiseManualLiveResizeSession,
    isHotTableColumnLiveResizeInProgress,
    markManualColumnLiveResizeSessionMoved,
    MilanoteHotTableCore,
} from '../utils/hotInstanceUtils/columnSize/hotTableManualColumnLiveResizeSessionUtils';
import { getHotTableColumnWidthsPx } from '../utils/hotInstanceUtils/columnSize/hotTableManualColumnResizeUtils';
import { getColumnWidthChangesAdjustedToGrid } from '../utils/dataUtils/columnSize/tableColumnResizeGridAlignmentUtils';
import { getNewColumnWidthsPx } from '../utils/dataUtils/columnSize/tableColumnResizeDistributionUtils';
import { repositionAutofillHandle } from '../utils/hotInstanceUtils/selection/hotTableSelectionUiUtils';

const COLUMN_RESIZING_CLASS_NAME = 'col-resizing';
const LIVE_RESIZE_POINTER_MOVE_THROTTLE_MS = 10;

type ManualColumnLiveResizeManager = {
    cleanup: () => void;
    finish: () => void;
    start: (thElement: HTMLTableCellElement) => (event: PointerEvent) => void;
};

/**
 * Owns the Milanote-specific live column resize gesture and its document event listeners.
 */
export const createManualColumnLiveResizeManager = ({
    hotInstance,
    gridSize,
    hotTableContainerRef,
    getContextZoomScale,
}: {
    hotInstance: MilanoteHotTableCore;
    gridSize: number;
    hotTableContainerRef: MutableRefObject<HTMLDivElement | null>;
    getContextZoomScale: () => number;
}): ManualColumnLiveResizeManager => {
    const move = (event: MouseEvent | PointerEvent): void => {
        if (!isHotTableColumnLiveResizeInProgress(hotInstance)) return;

        const liveResizeSession = getManualColumnLiveResizeSession(hotInstance);
        const selectedCols = liveResizeSession.selectedCols;

        if (!selectedCols.length) return;

        markManualColumnLiveResizeSessionMoved(hotInstance);

        const mousePos = event.clientX / getContextZoomScale();
        const diff = mousePos - liveResizeSession.dragStartX;
        const minTableCellWidthPx = TABLE_CELL_MIN_WIDTH * gridSize;
        const newColWidthsPx = getNewColumnWidthsPx(
            selectedCols,
            liveResizeSession.initialWidthsPx,
            minTableCellWidthPx,
            diff,
        );

        const columnWidthChanges = selectedCols.map((column, index) => ({
            column,
            widthPx: newColWidthsPx[index],
        }));

        applyManualColumnWidths(hotInstance, columnWidthChanges, { shouldSetTableElementDOMWidth: true });
    };

    const throttledMove = throttle(move, LIVE_RESIZE_POINTER_MOVE_THROTTLE_MS);

    const cleanup = (): void => {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        document.removeEventListener('pointerup', finish);
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        document.removeEventListener('pointercancel', finish);
        document.removeEventListener('pointermove', throttledMove);

        throttledMove.cancel();

        hotTableContainerRef.current?.classList.remove(COLUMN_RESIZING_CLASS_NAME);

        clearManualColumnLiveResizeSession(hotInstance);
    };

    const finish = (): void => {
        if (!isHotTableColumnLiveResizeInProgress(hotInstance)) return;

        const liveResizeSession = getManualColumnLiveResizeSession(hotInstance);

        cleanup();

        if (!liveResizeSession.moved) return;

        // Commit the drag result by rounding the affected widths and pushing any remaining
        // grid-alignment delta into the last resized column.
        const columnWidthChanges = getColumnWidthChangesAdjustedToGrid(
            getHotTableColumnWidthsPx(hotInstance),
            liveResizeSession.selectedCols,
            gridSize,
        );

        applyManualColumnWidthsWithHotHooks(hotInstance, columnWidthChanges);
        repositionAutofillHandle(hotInstance, hotTableContainerRef);
    };

    const start =
        (thElement: HTMLTableCellElement) =>
        (event: PointerEvent): void => {
            stopDefaultAndPropagation(event);

            // Ignore if double click, as this will be handled by on `onDoubleClickResize`
            const isDoubleClick = event.pointerType === 'mouse' && event.detail >= 2;
            if (isDoubleClick) return;

            const column = hotInstance.getCoords(thElement).col;
            if (column < 0) return;

            hotInstance.getActiveEditor()?.finishEditing();

            const selectedCols = getResizeTargetColumns(hotInstance.getSelected(), column);
            const initialWidthsPx = selectedCols.map((selectedCol) => hotInstance.getColWidth(selectedCol) || 0);

            initialiseManualLiveResizeSession(hotInstance, {
                selectedCols,
                initialWidthsPx,
                dragStartX: event.clientX / getContextZoomScale(),
            });

            hotTableContainerRef.current?.classList.add(COLUMN_RESIZING_CLASS_NAME);

            document.addEventListener('pointerup', finish);
            document.addEventListener('pointercancel', finish);
            document.addEventListener('pointermove', throttledMove);
        };

    return {
        start,
        finish,
        cleanup,
    };
};
